import React, { useState, useEffect } from 'react';
import { 
  Languages, Database, Search, Plus, Edit2, Trash2, Check, X, 
  BarChart3, RefreshCw, Download, Upload, Filter, CheckCircle2, AlertCircle
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../api';

const LANGUAGE_NAMES = {
  'en': 'English', 'es': 'Spanish', 'fr': 'French', 'zh': 'Chinese',
  'ru': 'Russian', 'uk': 'Ukrainian', 'vi': 'Vietnamese', 'tl': 'Filipino',
  'hi': 'Hindi', 'ur': 'Urdu', 'ja': 'Japanese', 'ko': 'Korean', 'th': 'Thai',
  'fa': 'Farsi', 'ar': 'Arabic', 'he': 'Hebrew', 'ga': 'Irish', 'de': 'German',
  'pt': 'Portuguese', 'it': 'Italian', 'pl': 'Polish', 'nl': 'Dutch',
  'af': 'Afrikaans', 'sw': 'Swahili', 'am': 'Amharic', 'om': 'Oromo',
  'ti': 'Tigrinya', 'ak': 'Twi', 'ha': 'Hausa', 'ig': 'Igbo', 'yo': 'Yoruba'
};

const TranslationMemoryPage = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVerified, setFilterVerified] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    original_text: '',
    target_language: 'es',
    translated_text: '',
    category: 'general',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterLanguage, filterCategory, filterVerified]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, statsRes, categoriesRes] = await Promise.all([
        api.get('/translate/memory', {
          params: {
            target_language: filterLanguage || undefined,
            category: filterCategory || undefined,
            verified_only: filterVerified || undefined,
            limit: 200
          }
        }),
        api.get('/translate/memory/stats'),
        api.get('/translate/memory/categories')
      ]);
      
      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data.stats || null);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error('Failed to load translation memory');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTranslations = async () => {
    try {
      const response = await api.post('/translate/memory/seed');
      toast.success(`Seeded ${response.data.message}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed translations');
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.original_text || !newEntry.translated_text) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api.post('/translate/memory', newEntry);
      toast.success('Translation added to memory');
      setShowAddModal(false);
      setNewEntry({
        original_text: '',
        target_language: 'es',
        translated_text: '',
        category: 'general',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add translation');
    }
  };

  const handleUpdateEntry = async (entryId, updateData) => {
    try {
      await api.put(`/translate/memory/${entryId}`, updateData);
      toast.success('Translation updated');
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update translation');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this translation?')) return;
    
    try {
      await api.delete(`/translate/memory/${entryId}`);
      toast.success('Translation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete translation');
    }
  };

  const handleVerifyEntry = async (entryId) => {
    await handleUpdateEntry(entryId, { is_verified: true });
  };

  const filteredEntries = entries.filter(entry => 
    entry.original_display?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.translated_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-7 h-7 text-[#ea580c]" />
              Translation Memory
            </h1>
            <p className="text-gray-600 mt-1">
              Manage cached translations for consistent clinical terminology
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSeedTranslations}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              Seed Clinical Terms
            </Button>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-[#ea580c] hover:bg-[#dc5612] gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Translation
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Entries</p>
                  <p className="text-xl font-bold text-gray-900">{stats.total_entries}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Verified</p>
                  <p className="text-xl font-bold text-gray-900">{stats.verified_entries}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Languages className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Languages</p>
                  <p className="text-xl font-bold text-gray-900">
                    {Object.keys(stats.by_language || {}).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Verification Rate</p>
                  <p className="text-xl font-bold text-gray-900">{stats.verification_rate}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search translations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
            >
              <option value="">All Languages</option>
              {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterVerified}
                onChange={(e) => setFilterVerified(e.target.checked)}
                className="rounded border-gray-300 text-[#ea580c] focus:ring-[#ea580c]"
              />
              <span className="text-sm text-gray-600">Verified only</span>
            </label>
            <Button variant="ghost" onClick={fetchData} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Translations Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Original</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Translation</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Language</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Uses</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No translations found. Click "Seed Clinical Terms" to add pre-defined translations.
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{entry.original_display}</span>
                      </td>
                      <td className="px-4 py-3">
                        {editingEntry === entry.id ? (
                          <input
                            type="text"
                            defaultValue={entry.translated_text}
                            className="w-full px-2 py-1 border rounded"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateEntry(entry.id, { translated_text: e.target.value });
                              } else if (e.key === 'Escape') {
                                setEditingEntry(null);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span className="text-gray-700">{entry.translated_text}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {LANGUAGE_NAMES[entry.target_language] || entry.target_language}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {entry.category?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-600">{entry.usage_count || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.is_verified ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-400">
                            <AlertCircle className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!entry.is_verified && (
                            <button
                              onClick={() => handleVerifyEntry(entry.id)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Verify translation"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setEditingEntry(entry.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit translation"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete translation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Used Translations */}
        {stats?.top_used && stats.top_used.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Used Translations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.top_used.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{item.original_display}</p>
                    <p className="text-sm text-gray-500">
                      {LANGUAGE_NAMES[item.target_language]}: {item.translated_text}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-[#ea580c] text-white rounded text-xs font-bold">
                    {item.usage_count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Translation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Translation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Original Text (English) *
                </label>
                <input
                  type="text"
                  value={newEntry.original_text}
                  onChange={(e) => setNewEntry({ ...newEntry, original_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
                  placeholder="e.g., Blood Pressure"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Language *
                </label>
                <select
                  value={newEntry.target_language}
                  onChange={(e) => setNewEntry({ ...newEntry, target_language: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
                >
                  {Object.entries(LANGUAGE_NAMES).filter(([code]) => code !== 'en').map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Translated Text *
                </label>
                <input
                  type="text"
                  value={newEntry.translated_text}
                  onChange={(e) => setNewEntry({ ...newEntry, translated_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
                  placeholder="e.g., Presión Arterial"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={newEntry.category}
                  onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#ea580c]"
                  rows="2"
                  placeholder="Optional notes about this translation"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddEntry}
                className="bg-[#ea580c] hover:bg-[#dc5612]"
              >
                Add Translation
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TranslationMemoryPage;
